/**
 * Location Utilities
 * Shared functions for ZIP/location handling across the theme
 * Consolidates duplicate code from store-picker.js, product-delivery-checker.js,
 * product-location-filter.js, and store-locator.js
 */
window.LocationUtils = (function() {
  const LOCATION_KEY = 'soil3_location';
  const STORE_KEY = 'soil3_store';

  return {
    // ============ CONSTANTS ============
    LOCATION_KEY: LOCATION_KEY,
    STORE_KEY: STORE_KEY,

    // ============ VALIDATION ============
    isValidZip(zip) {
      return /^\d{5}$/.test(zip);
    },

    // ============ ZIP PARSING ============
    parseZipRanges(zipString) {
      if (!zipString) return [];
      const zips = [];
      const parts = zipString.split(',').map(s => s.trim());

      parts.forEach(part => {
        if (part === 'default' || part === 'all') return;

        if (part.includes('-')) {
          const [start, end] = part.split('-').map(z => parseInt(z.trim()));
          if (!isNaN(start) && !isNaN(end)) {
            for (let i = start; i <= end; i++) {
              zips.push(i.toString().padStart(5, '0'));
            }
          }
        } else if (/^\d{5}$/.test(part)) {
          zips.push(part);
        }
      });

      return zips;
    },

    isZipInRange(zipString, userZip) {
      if (!zipString || !userZip) return false;
      return this.parseZipRanges(zipString).includes(userZip);
    },

    // ============ DISTANCE ============
    calculateDistance(lat1, lon1, lat2, lon2) {
      if ([lat1, lon1, lat2, lon2].some(v => typeof v !== 'number' || isNaN(v))) {
        return Infinity;
      }
      const R = 3958.8; // Earth radius in miles
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return R * c;
    },

    // ============ STORAGE: LOCATION ============
    getLocation() {
      try {
        const data = localStorage.getItem(LOCATION_KEY);
        return data ? JSON.parse(data) : null;
      } catch (e) {
        return null;
      }
    },

    setLocation(zipCode, source = 'zip', coordinates = null) {
      const data = {
        zipCode,
        source,
        coordinates,
        timestamp: Date.now()
      };
      localStorage.setItem(LOCATION_KEY, JSON.stringify(data));

      // Dispatch event
      document.dispatchEvent(new CustomEvent('location:changed', { detail: data }));

      return data;
    },

    getZipCode() {
      const location = this.getLocation();
      return location?.zipCode || null;
    },

    clearLocation() {
      localStorage.removeItem(LOCATION_KEY);
      document.dispatchEvent(new CustomEvent('location:changed', { detail: null }));
    },

    // ============ STORAGE: STORE ============
    getStore() {
      try {
        const data = localStorage.getItem(STORE_KEY);
        return data ? JSON.parse(data) : null;
      } catch (e) {
        return null;
      }
    },

    setStore(id, name, zip) {
      console.log('[6] LocationUtils.setStore called with:', id, name, zip);
      const data = { id, name, zip };
      localStorage.setItem(STORE_KEY, JSON.stringify(data));

      // Dispatch event
      console.log('[6] Dispatching store:selected event with:', data);
      document.dispatchEvent(new CustomEvent('store:selected', { detail: data }));

      window.LocationUtils.syncCartStore(name);
      return data;
    },

    clearStore() {
      localStorage.removeItem(STORE_KEY);
      window.LocationUtils.syncCartStore('');
    },

    /**
     * Mirrors the chosen store into the `pickup_location` cart attribute, which the
     * "Super Sod Checkout Rules" app reads at checkout to hide every other pickup location.
     * window.cartPickupLocation is rendered from the cart in theme.liquid, so this only
     * posts when the value actually changes.
     */
    syncCartStore(name) {
      const value = name || '';
      if (value === (window.cartPickupLocation || '')) return;
      window.cartPickupLocation = value;
      fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { pickup_location: value } })
      }).catch(() => {});
    },

    /**
     * If the cart has a "Pick It Up" item, records PICK_UP as the cart's preferred delivery
     * method (Storefront API, tokenless) so checkout opens on the Pickup tab instead of Ship.
     */
    async preferPickupIfNeeded() {
      const cart = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js').then(r => r.json());
      const isPickup = item => /pick\s*(it\s*)?up/i.test([item.variant_title, ...(item.variant_options || [])].join(' '));
      if (!cart.items?.some(isPickup)) return;
      await fetch('/api/2026-07/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: 'mutation($id: ID!, $buyer: CartBuyerIdentityInput!) { cartBuyerIdentityUpdate(cartId: $id, buyerIdentity: $buyer) { userErrors { message } } }',
          variables: {
            id: 'gid://shopify/Cart/' + cart.token,
            buyer: { preferences: { delivery: { deliveryMethod: ['PICK_UP'] } } }
          }
        })
      });
    },

    // ============ IP GEOLOCATION ============
    /**
     * Detect user location from IP address using ipapi.co
     * Works even in incognito since it uses server-side IP lookup
     * @returns {Promise<{zipCode, latitude, longitude, city, region}|null>}
     */
    async detectLocationFromIP() {
      try {
        const response = await fetch('https://ipapi.co/json/');
        if (!response.ok) return null;

        const data = await response.json();

        // ipapi returns: postal, latitude, longitude, city, region, country_code, etc.
        if (data.postal && this.isValidZip(data.postal)) {
          return {
            zipCode: data.postal,
            latitude: data.latitude,
            longitude: data.longitude,
            city: data.city,
            region: data.region
          };
        }
        return null;
      } catch (e) {
        console.warn('IP geolocation failed:', e);
        return null;
      }
    },

    /**
     * Auto-detect and set location if none exists
     * Call this on page load
     */
    async autoDetectLocation() {
      // Skip if user already has a location set
      if (this.getZipCode()) return null;

      const detected = await this.detectLocationFromIP();
      if (detected) {
        this.setLocation(
          detected.zipCode,
          'ip_geolocation',
          { lat: detected.latitude, lng: detected.longitude }
        );

        // Dispatch event with coordinates for store finder
        document.dispatchEvent(new CustomEvent('location:auto-detected', {
          detail: {
            zipCode: detected.zipCode,
            latitude: detected.latitude,
            longitude: detected.longitude
          }
        }));

        return detected;
      }
      return null;
    },

    // ============ MIGRATION ============
    // One-time migration from old keys to new format
    migrateFromLegacy() {
      // Always clean up old keys (prevents stale data issues)
      const oldKeys = ['userSearchLocation', 'userSearchLocationType', 'userSearchCoordinates',
       'userStoreId', 'userStoreName', 'userLocation', 'selectedStoreZip', 'userStoreZip'];

      // Check if already migrated - just clean up and return
      if (localStorage.getItem(LOCATION_KEY)) {
        oldKeys.forEach(key => localStorage.removeItem(key));
        return;
      }

      // Try to get ZIP from old keys before we delete them
      let zipCode = null;
      for (const key of ['userSearchLocation', 'userLocation', 'selectedStoreZip']) {
        const value = localStorage.getItem(key);
        if (value && /^\d{5}$/.test(value)) {
          zipCode = value;
          break;
        }
      }

      // Migrate location
      if (zipCode) {
        const source = localStorage.getItem('userSearchLocationType') || 'zip';
        let coordinates = null;
        try {
          coordinates = JSON.parse(localStorage.getItem('userSearchCoordinates'));
        } catch (e) {}

        // Set without dispatching event during migration
        const data = {
          zipCode,
          source,
          coordinates,
          timestamp: Date.now()
        };
        localStorage.setItem(LOCATION_KEY, JSON.stringify(data));
      }

      // Migrate store
      const storeId = localStorage.getItem('userStoreId');
      const storeName = localStorage.getItem('userStoreName');
      if (storeId && storeName) {
        const storeData = { id: storeId, name: storeName, zip: zipCode };
        localStorage.setItem(STORE_KEY, JSON.stringify(storeData));
      }

      // Clean up old keys
      oldKeys.forEach(key => localStorage.removeItem(key));
    }
  };
})();

// Run migration and auto-detect on load
document.addEventListener('DOMContentLoaded', () => {
  LocationUtils.migrateFromLegacy();

  // Stores chosen before the cart attribute existed (or a fresh cart after checkout)
  LocationUtils.syncCartStore(LocationUtils.getStore()?.name);

  // Auto-detect location from IP if none set
  LocationUtils.autoDetectLocation();
});

// Checkout buttons (cart page + drawer) are `name="checkout"` submit buttons. Bubble phase so the
// theme's own handlers (e.g. terms checkbox) run first; if they block checkout, do nothing.
// Never waits more than 2.5s before continuing to checkout.
document.addEventListener('submit', (event) => {
  const submitter = event.submitter;
  if (event.defaultPrevented || !submitter || submitter.name !== 'checkout' || submitter.dataset.pickupPreferred) return;
  event.preventDefault();
  const form = event.target;
  Promise.race([LocationUtils.preferPickupIfNeeded(), new Promise(resolve => setTimeout(resolve, 2500))])
    .catch(() => {})
    .finally(() => {
      submitter.dataset.pickupPreferred = '1';
      form.requestSubmit(submitter);
      delete submitter.dataset.pickupPreferred;
    });
});

// Blocks adding a "Pick Up" variant the customer's selected store doesn't stock. Every add-to-cart
// path on this theme (product form, collection quick-add, quick view) calls fetch('/cart/add.js'),
// so the check wraps fetch. Answers with Shopify's own 422 shape, which the product form already
// displays, plus a toast for callers that only show a generic error. Fails open on lookup errors.
(function () {
  const nativeFetch = window.fetch.bind(window);
  const stripSuffix = name => (name || '').replace(/\s*\(.*\)\s*$/, '').trim().toLowerCase();

  function variantIdsFrom(body) {
    try {
      if (typeof body === 'string' && body.trim().startsWith('{')) {
        const data = JSON.parse(body);
        return (data.items || [data]).map(item => item.id).filter(Boolean);
      }
      // Product form (theme.js) posts urlencoded text: "id=123&quantity=1&..."
      if (typeof body === 'string') body = new URLSearchParams(body);
      if (body instanceof FormData || body instanceof URLSearchParams) {
        return body.getAll('id').concat(body.getAll('items[][id]')).filter(Boolean);
      }
    } catch (e) {}
    return [];
  }

  async function unavailableMessage(ids, storeName) {
    const query = 'query($ids: [ID!]!) { nodes(ids: $ids) { ... on ProductVariant { title product { title } ' +
      'storeAvailability(first: 100) { nodes { available location { name } } } } } }';
    const res = await nativeFetch('/api/2026-07/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables: { ids: ids.map(id => 'gid://shopify/ProductVariant/' + id) } })
    });
    const variants = (await res.json()).data?.nodes || [];
    const store = stripSuffix(storeName);
    const blocked = variants.find(v => v && /pick\s*(it\s*)?up/i.test(v.title) &&
      !v.storeAvailability.nodes.some(s => s.available && stripSuffix(s.location.name) === store));
    return blocked && `${blocked.product.title.split('|')[0].trim()} isn't available for pickup at ${storeName}. ` +
      'Choose another store or switch to delivery.';
  }

  function toast(message) {
    const el = document.createElement('div');
    el.setAttribute('role', 'alert');
    el.textContent = message;
    el.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483647;' +
      'max-width:min(90vw,480px);padding:14px 18px;border-radius:8px;background:#1f1f1f;color:#fff;' +
      'font-size:15px;line-height:1.4;box-shadow:0 4px 16px rgba(0,0,0,.25)';
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 6000);
  }

  window.fetch = async function (input, init) {
    const url = typeof input === 'string' ? input : input?.url || '';
    const storeName = window.LocationUtils?.getStore()?.name;
    if (!storeName || !/\/cart\/add(\.js)?(\?|$)/.test(url) || (init?.method || 'GET').toUpperCase() !== 'POST') {
      return nativeFetch(input, init);
    }
    let message = null;
    try {
      const ids = variantIdsFrom(init.body);
      if (ids.length) message = await unavailableMessage(ids, storeName);
    } catch (e) {}
    if (!message) return nativeFetch(input, init);
    toast(message);
    return new Response(JSON.stringify({ status: 422, message: 'Cart Error', description: message }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' }
    });
  };
})();
